/**
 * Global factory project picker (beatly / devtools under the team).
 * Ghost TopBar menu so WIP, roadmap, and automations share one scope.
 */

import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useProject } from '../hooks/useProject';
import { routes } from '../utils/navigation';
import { ShellMenu } from './ui/ShellMenu';

/**
 * Top-bar factory project picker; empty state links to Team → Projects.
 * Writes the active project via useProject.setProjectId (localStorage-backed).
 */
export function ProjectSwitcher() {
  const { t } = useTranslation();
  const { projects, projectId, setProjectId, isLoading, project } = useProject();

  if (isLoading && !projects.length) {
    return (
      <span className="text-xs text-[var(--muted)]" data-testid="project-switcher-loading">
        {t('shell.projectLoading')}
      </span>
    );
  }

  if (!projects.length) {
    return (
      <span className="flex items-center gap-2 text-xs text-[var(--muted)]" data-testid="project-switcher-empty">
        {t('shell.projectNone')}
        <Link
          to={routes.teamProjects()}
          className="text-[var(--muted)] hover:text-[var(--foreground)] hover:underline"
        >
          {t('shell.manageProjects')}
        </Link>
      </span>
    );
  }

  const triggerLabel = project?.name || projects.find((p) => p.project_id === projectId)?.name || projectId;

  return (
    <ShellMenu
      data-testid="project-switcher"
      ariaLabel={t('shell.projectSwitcherAria')}
      triggerLabel={triggerLabel}
      items={projects.map((p) => ({
        id: p.project_id,
        label: p.name,
        selected: p.project_id === projectId,
      }))}
      onSelect={setProjectId}
      footer={
        <Link
          to={routes.teamProjects()}
          className="text-[var(--muted)] hover:text-[var(--foreground)] hover:underline"
          data-testid="project-switcher-manage"
        >
          {t('shell.manageProjects')}
        </Link>
      }
    />
  );
}
